import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { postGLForm } from '../../../../api/invoicing';
import { getGLs, getVendors } from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import FormTextWithValue from '../../../../reusable-components/form/FormTextWithValue';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { ScopeGuard } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import AddMoreCategoryAutocomplete from './AddMoreCategoryAutocomplete';
import NewGLDeptModal from './NewGLDeptModal';

NewGLModal.propTypes = {
  row: PropTypes.object,
  edit: PropTypes.bool,
};

const menuItemSx = {
  p: 0,
  minHeight: 'unset',
  fontSize: '14px',
  color: 'grey.700',
  justifyContent: 'flex-start',
  lineHeight: '20px',
  textTransform: 'none',
  height: 40,
  borderRadius: 1,
  pl: 2,
  pr: 2,
};

export default function NewGLModal({ row = null, edit = false }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [edited, setEdited] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [checked, setChecked] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: { invoicingGLs, editableGLFacilities },
  } = useSelector((state) => state.invoicing);

  const [openDeptDialog, setOpenDeptDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const handleClickOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const facOptions = useMemo(() => editableGLFacilities, [editableGLFacilities]);
  const existingGLs = useMemo(
    () =>
      invoicingGLs?.map((v) => ({
        id: v.subcategoryId,
        facilityId: v.facilityId,
        glCode: v.glCode?.toLowerCase()?.trim(),
        subcategory: v.subcategory?.toLowerCase()?.trim(),
      })),
    [invoicingGLs]
  );

  const [value, setValue] = useState({
    id: '',
    glCode: '',
    facility: '',
    category: '',
    categoryId: '',
    categoryDesc: '',
    subcategory: '',
    deleteCats: [],
  });

  const handleSave = async () => {
    const response = await postGLForm(value);
    if (response.data.error) {
      enqueueSnackbar(`Error: ${response.data.error}`, {
        variant: 'error',
      });
    } else if (response.status === 200) {
      enqueueSnackbar(`Successfully added ${response.data.code}`, {
        variant: 'success',
      });
      dispatch(getVendors());
      dispatch(getGLs());
      setOpen(false);
    } else
      enqueueSnackbar(`Error: Could not add vendor`, {
        variant: 'error',
      });
  };

  const handleClickOpen = () => {
    handleCloseMenu();
    setValue({
      id: edit ? row.subcategoryId : '',
      glCode: edit ? row.glCode : '',
      facility: edit ? facOptions?.find((f) => f.value === row.facilityId) ?? '' : '',
      categoryDesc: edit ? row.categoryDesc : '',
      category: edit ? row.category : '',
      categoryId: edit ? row.categoryId : '',
      subcategory: edit ? row.subcategory : '',
      deleteCats: [],
    });
    setOpen(true);
  };
  const handleClose = () => {
    setValue({
      id: '',
      glCode: '',
      category: '',
      categoryId: '',
      categoryDesc: '',
      subcategory: '',
      deleteCats: [],
    });
    setOpen(false);
  };
  const glError =
    value.glCode === '' ||
    existingGLs?.some(
      (g) =>
        g.id !== (edit ? row.subcategoryId : undefined) &&
        g.glCode === value.glCode?.toLowerCase().trim() &&
        g.subcategory === value.subcategory?.toLowerCase()?.trim() &&
        (g.facilityId === facOptions?.find((o) => o.value === value.facility?.value)?.value || g.facilityId === 0)
    );
  const facilityError = value.facility === '' || value.facility === undefined || value.facility === null;

  function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  useEffect(() => {
    if (open) {
      const onEdit = !Object.keys(value).every((key) => {
        let value1 = value?.[key];
        let value2 = edit ? row[key] : '';
        if (isObject(value1)) value1 = value1.label;
        if (isObject(value2)) value2 = value2.label;

        if (!Number.isNaN(+value2)) return Number(value1) === Number(value2);
        return (value1 === null && value2 === '') || (value1 === '' && value2 === null) || value1 === value2;
      });
      setEdited(onEdit);
    }
  }, [value]);

  useEffect(() => {
    if (!edit) setChecked(!!value?.facility?.extraValue);
  }, [value.facility]);

  useEffect(() => {
    if (edit) setChecked(!!value?.categoryId);
  }, [open]);

  useEffect(() => {
    if (checked === false) setValue((v) => ({ ...v, category: '', categoryDesc: '', categoryId: 0, deleteCats: [] }));
  }, [checked]);

  return (
    <ScopeGuard scopes={['Edit-Invoice-GLs']} allowAdmin>
      {edit ? (
        row.glCode ? (
          <Tooltip title="edit gl">
            {' '}
            <IconButton variant="outlined" sx={{ mr: 1 }} size="small" color="secondary" onClick={handleClickOpen}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <></>
        )
      ) : (
        <Button
          variant="outlined"
          sx={{ mr: 1 }}
          size="small"
          color="secondary"
          onClick={handleClickOpenMenu}
          endIcon={<ArrowDropDownIcon />}
        >
          New
        </Button>
      )}

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            sx: {
              width: 130,
              p: 1,
              borderRadius: '5px',
              boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
            },
          },
          list: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              p: 0,
            },
            '&:hover': {
              bgcolor: 'grey.200',
            },
          },
        }}
      >
        <MenuItem onClick={handleClickOpen} sx={menuItemSx}>
          GL Code
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseMenu();
            setOpenDeptDialog(true);
          }}
          sx={menuItemSx}
        >
          Department
        </MenuItem>
      </Menu>
      <NewGLDeptModal row={row} edit={edit} open={openDeptDialog} setOpen={setOpenDeptDialog} />
      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }} maxWidth="xl">
        <DialogTitle sx={{ textAlign: 'center' }}>
          {edit ? 'Edit Invoice GL' : 'New Invoice GL'}
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Grid container direction="column" spacing={2} sx={{ px: 2 }}>
              <Grid item>
                {edited && (glError || facilityError) && (
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {value.glCode === '' ? 'Missing GL' : glError && 'GL already exists'}
                    {glError && facilityError && ' & '}
                    {facilityError && 'Missing facility'}
                  </Alert>
                )}
              </Grid>
              <Grid item>
                <Box flex={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Facility *
                  </Typography>
                  <Autocomplete
                    size="small"
                    fullWidth
                    value={value.facility}
                    isOptionEqualToValue={(option, value) => option?.id === value?.facility?.id}
                    options={facOptions || []}
                    onChange={(e, newValue) => setValue({ ...value, facility: newValue })}
                    renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
                    sx={{
                      width: '100%',
                      mt: 1,
                      '& legend': { display: 'none' },
                      '& fieldset': { top: 0 },
                      '& .MuiFormLabel-root ': { display: 'none' },
                    }}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={<Switch sx={{ mt: 0 }} checked={checked} onChange={() => setChecked((c) => !c)} />}
                    label={'Show Department'}
                  />
                  <Divider />
                </Box>
              </Grid>
              <Grid item>
                {checked && (
                  <>
                    <Box>
                      <AddMoreCategoryAutocomplete value={value} setValue={setValue} setError={setCategoryError} />
                    </Box>
                    <Divider sx={{ mt: 3 }} />
                  </>
                )}
              </Grid>
              <Grid item container direction="row" spacing={2}>
                <FormTextWithValue value={value} title="GL Code Number *" flex={6} name="glCode" setValue={setValue} />
                <FormTextWithValue value={value} title="Name" flex={6} name="subcategory" setValue={setValue} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            disabled={glError || facilityError || (checked && categoryError)}
            sx={{ width: 150 }}
            onClick={handleSave}
          >
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </ScopeGuard>
  );
}
